
import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import { blogPosts } from '../../../mocks/blogPosts';

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState<any>(null);
  const [relatedPosts, setRelatedPosts] = useState<any[]>([]);

  useEffect(() => {
    const foundPost = blogPosts.find(p => p.slug === slug);
    if (foundPost) {
      setPost(foundPost);
      
      // Find related posts from same category
      const related = blogPosts
        .filter(p => p.category === foundPost.category && p.id !== foundPost.id)
        .slice(0, 3);
      setRelatedPosts(related);

      // Update page title and meta description
      document.title = `${foundPost.title} - XJ Global Blog`;
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', foundPost.excerpt);
      }

      // Add Schema.org JSON-LD
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.text = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        "headline": foundPost.title,
        "description": foundPost.excerpt,
        "image": foundPost.image,
        "url": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/blog/${foundPost.slug}`,
        "datePublished": foundPost.publishDate,
        "dateModified": foundPost.publishDate,
        "author": {
          "@type": "Person",
          "name": foundPost.author
        },
        "publisher": {
          "@type": "Organization",
          "name": "XJ Global",
          "logo": {
            "@type": "ImageObject",
            "url": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/logo.png`
          }
        },
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/blog/${foundPost.slug}`
        }
      });
      document.head.appendChild(script);

      return () => {
        document.head.removeChild(script);
      };
    }
  }, [slug]);

  if (!post) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="flex flex-col items-center justify-center h-screen text-center px-4">
          <h1 className="text-5xl md:text-5xl font-semibold text-gray-100">404</h1>
          <h1 className="text-2xl md:text-3xl font-semibold mt-6">Blog post not found</h1>
          <p className="mt-4 text-xl md:text-2xl text-gray-500">The blog post you're looking for doesn't exist.</p>
          <Link 
            to="/blog"
            className="mt-6 inline-flex items-center px-6 py-3 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
          >
            Back to Blog
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Breadcrumb */}
      <div className="bg-gray-50 py-4 mt-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center space-x-2 text-sm">
            <Link to="/" className="text-gray-500 hover:text-gray-700 cursor-pointer">Home</Link>
            <i className="ri-arrow-right-s-line text-gray-400"></i>
            <Link to="/blog" className="text-gray-500 hover:text-gray-700 cursor-pointer">Blog</Link>
            <i className="ri-arrow-right-s-line text-gray-400"></i>
            <span className="text-gray-900">{post.title}</span>
          </nav>
        </div>
      </div>

      {/* Article Header */}
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <header className="mb-8">
          <div className="flex items-center mb-4">
            <span className="bg-teal-100 text-teal-800 text-sm font-medium px-3 py-1 rounded-full">
              {post.category}
            </span>
            <span className="ml-4 text-sm text-gray-500">{post.readTime}</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
            {post.title}
          </h1>
          
          <div className="flex items-center justify-between border-b border-gray-200 pb-6">
            <div className="flex items-center">
              <img 
                src={`https://public.readdy.ai/ai/img_res/433a6a8e6a943fc5d872f96127c3a9e5.jpg$%7Bpost.author%7D%20headshot%20corporate%20executive&width=48&height=48&seq=author-${post.id}-large&orientation=squarish`}
                alt={post.author}
                className="w-12 h-12 rounded-full object-cover object-top mr-4"
              />
              <div>
                <p className="font-medium text-gray-900">{post.author}</p>
                <p className="text-sm text-gray-500">Published on {post.publishDate}</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <button className="text-gray-400 hover:text-gray-600 cursor-pointer">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-share-line"></i>
                </div>
              </button>
              <button className="text-gray-400 hover:text-gray-600 cursor-pointer">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-bookmark-line"></i>
                </div>
              </button>
            </div>
          </div>
        </header>

        {/* Featured Image */}
        <div className="mb-8">
          <img 
            src={post.image} 
            alt={post.title}
            className="w-full h-96 object-cover object-top rounded-xl"
          />
        </div>

        {/* Article Content */}
        <div className="prose prose-lg max-w-none">
          <p className="text-xl text-gray-600 mb-8 font-medium leading-relaxed">
            {post.excerpt}
          </p>
          
          <div className="space-y-6 text-gray-800 leading-relaxed">
            {post.content.split('\n\n').map((paragraph: string, index: number) => {
              if (paragraph.startsWith('##')) {
                return (
                  <h2 key={index} className="text-2xl font-bold text-gray-900 mt-8 mb-4">
                    {paragraph.replace('## ', '')}
                  </h2>
                );
              } else if (paragraph.startsWith('###')) {
                return (
                  <h3 key={index} className="text-xl font-bold text-gray-900 mt-6 mb-3">
                    {paragraph.replace('### ', '')}
                  </h3>
                );
              } else if (paragraph.startsWith('- ')) {
                const listItems = paragraph.split('\n').filter(item => item.startsWith('- '));
                return (
                  <ul key={index} className="list-disc pl-6 space-y-2">
                    {listItems.map((item, itemIndex) => (
                      <li key={itemIndex}>{item.replace('- ', '')}</li>
                    ))}
                  </ul>
                );
              } else {
                return (
                  <p key={index} className="mb-4">
                    {paragraph}
                  </p>
                );
              }
            })}
          </div>
        </div>

        {/* Tags */}
        <div className="mt-12 pt-8 border-t border-gray-200">
          <div className="flex flex-wrap gap-2">
            {post.tags.map((tag: string, index: number) => (
              <span 
                key={index}
                className="bg-gray-100 text-gray-700 text-sm px-3 py-1 rounded-full"
              >
                #{tag}
              </span>
            ))}
          </div>
        </div>
      </article>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="bg-gray-50 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              Related Articles
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              {relatedPosts.map((relatedPost) => (
                <article key={relatedPost.id} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                  <img 
                    src={relatedPost.image} 
                    alt={relatedPost.title}
                    className="w-full h-48 object-cover object-top"
                  />
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <span className="bg-gray-100 text-gray-700 text-sm font-medium px-3 py-1 rounded-full">
                        {relatedPost.category}
                      </span>
                      <span className="text-sm text-gray-500">{relatedPost.readTime}</span>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2">
                      {relatedPost.title}
                    </h3>
                    <p className="text-gray-600 mb-4 line-clamp-2">
                      {relatedPost.excerpt}
                    </p>
                    <Link 
                      to={`/blog/${relatedPost.slug}`}
                      className="text-teal-600 hover:text-teal-700 font-medium text-sm cursor-pointer whitespace-nowrap"
                    >
                      Read More →
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
